import React from 'react';
import type { EventNameNavProps } from '../components/EventNameNav';
import { EventNameNav } from '../components/EventNameNav';
import type { WebsiteHeaderLayout_Monogram } from '@shared/types/WebsiteHeaderSettings.types';
import { HeaderPhoto } from '../components/HeaderPhoto/HeaderPhoto';
import { useJsonDataContainerByIdQuery } from '../../../../../../../../websiteCustomJsonComponents/customComponents/JsonWrapper.graphql.generated';
import { Box, Flex } from '@withjoy/joykit';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
// eslint-disable-next-line no-restricted-imports -- imports lazy wrapper from admin
import { LazyLoadedMonogramView } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/components/MonogramEditorDialog/MonogramView.lazy';
import { useMonogramPersonalizationOverrides } from '../hooks/useMonogramPersonalizationOverrides';

interface MonogramLayoutProps extends EventNameNavProps {
  websiteHeader: WebsiteHeaderLayout_Monogram;
  showPlaceholder?: boolean;
}

export const MonogramLayout = (props: MonogramLayoutProps) => {
  const { websiteHeader, showPlaceholder = false, ...restProps } = props;

  const layout = websiteHeader.headerPhoto;
  const jsonContainerId = layout.jsonContainerId;

  const { data } = useJsonDataContainerByIdQuery({
    variables: {
      payload: {
        id: jsonContainerId as string,
      },
    },
    batchMode: 'fast',
  });

  const jsonContent = data?.jsonDataContainerById?.content;

  const { initials: previewInitials, eventDate: previewEventDate } = useMonogramPersonalizationOverrides();

  return (
    <EventNameNav
      eventDisplayName={websiteHeader.eventDisplayName}
      {...restProps}
    >
      <HeaderPhoto
        websiteHeader={websiteHeader}
        pageSlug={restProps.pageSlug}
        pageType={restProps.page}
        showPlaceholder={showPlaceholder}
      >
        <Monogram
          config={jsonContent}
          showPlaceholder={showPlaceholder}
          previewInitials={previewInitials}
          previewEventDate={previewEventDate}
        />
      </HeaderPhoto>
    </EventNameNav>
  );
};

// 6 inches × 72 PPI (points per inch, standard screen resolution constant)
const SIZE = 6 * 72;

const Monogram = ({
  config,
  showPlaceholder,
  previewInitials,
  previewEventDate,
}: {
  config?: ThemeJson;
  showPlaceholder?: boolean;
  previewInitials?: ReadonlyArray<string>;
  previewEventDate?: number | null;
}) => {
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  if (!config) {
    return showPlaceholder ? (
      <Flex
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Box
          width={SIZE}
          height={SIZE}
          backgroundColor="lightgray"
        />
      </Flex>
    ) : null;
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <LazyLoadedMonogramView
        json={config}
        background={pageColor}
        fontColor={pageFontColor}
        initials={previewInitials}
        eventDate={previewEventDate}
      />
    </Flex>
  );
};
